import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MEDVi-style infinite marquee testimonial slider.
 * - Duplicates the list to create a seamless loop
 * - Pauses on hover via `group-hover:[animation-play-state:paused]`
 * - Deep rounded corners, soft shadows, primary star icons
 */
export default function TestimonialsMarquee() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => Array.isArray(r.data) && setItems(r.data))
      .catch(() => {});
  }, []);

  if (!items.length) return null;

  // Duplicate for seamless loop
  const loop = [...items, ...items];

  return (
    <div
      data-testid="testimonials-marquee"
      className="group relative w-full overflow-hidden"
    >
      {/* Edge gradients */}
      <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[#FDFBF7] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[#FDFBF7] to-transparent z-10 pointer-events-none" />

      <div className="flex gap-6 md:gap-8 animate-marquee-slow whitespace-normal group-hover:[animation-play-state:paused] py-4">
        {loop.map((t, i) => (
          <TestimonialCard key={`${t.id || t.name}-${i}`} t={t} idx={i} />
        ))}
      </div>
    </div>
  );
}

function TestimonialCard({ t, idx }) {
  return (
    <article
      data-testid={`testimonial-card-${idx}`}
      className="flex-shrink-0 w-[300px] md:w-[380px] bg-white border border-stone-200 rounded-3xl p-7 md:p-8 shadow-[0_8px_30px_-12px_rgba(243,146,55,0.15)] hover:shadow-[0_20px_50px_-12px_rgba(243,146,55,0.35)] hover:-translate-y-1 transition-all duration-500"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-[#F5F2EB] flex-shrink-0 border border-stone-200">
          {t.avatar_url ? (
            <img src={t.avatar_url} alt={t.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-lg text-[#F39237]">
              {(t.name || "?").charAt(0)}
            </div>
          )}
        </div>
        <div>
          <div className="font-display text-base text-[#0A0A0A] leading-tight">
            {t.name}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            {t.city}
            {t.tier ? ` · ${t.tier}` : ""}
          </div>
        </div>
      </div>

      <div className="text-[#F39237] mb-4 flex gap-1">
        {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, k) => (
          <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
        ))}
      </div>

      <p className="font-display italic text-base md:text-lg leading-relaxed text-[#0A0A0A]">
        &ldquo;{t.quote}&rdquo;
      </p>
    </article>
  );
}
